import { supabase } from '@/integrations/supabase/client';

export type ManualPlatform = 'instagram' | 'tiktok' | 'x' | 'youtube';

export interface ManualCountRow {
  id: string;
  user_id: string;
  platform: ManualPlatform | string;
  follower_count: number;
  created_at: string;
  updated_at: string;
}

export async function getManualCounts(userId: string): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('social_manual_counts')
    .select('platform,follower_count')
    .eq('user_id', userId);

  if (error) {
    // Relation does not exist -> migration not applied yet; fail gracefully
    // Postgres error code 42P01
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyErr: any = error as any;
    if (anyErr?.code === '42P01') {
      console.debug('[social-manual-service] Table social_manual_counts missing; returning empty counts');
      return {};
    }
    throw error;
  }

  const map: Record<string, number> = {};
  (data || []).forEach((row: any) => {
    map[row.platform] = Number(row.follower_count) || 0;
  });
  return map;
}

export async function upsertManualCount(userId: string, platform: ManualPlatform, followerCount: number) {
  const normalized = Math.max(0, Math.floor(Number(followerCount) || 0));
  const { error } = await supabase
    .from('social_manual_counts')
    .upsert({ user_id: userId, platform, follower_count: normalized, updated_at: new Date().toISOString() }, { onConflict: 'user_id,platform' });
  if (error) {
    console.warn('[social-manual-service] upsertManualCount failed', { code: (error as any)?.code, message: error.message });
    throw error;
  }
}

export function sumManualCounts(counts: Record<string, number>): number {
  return Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0);
}

// Minimal connectivity check (no schema changes or extra features)
export async function isSupabaseConnected(): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('profiles')
      .select('id', { head: true, count: 'exact' })
      .limit(1);
    return !error;
  } catch {
    return false;
  }
}
